import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, Minus, Check, X, LogOut, AlertCircle, Search } from 'lucide-react';
import { useTenant } from '../../lib/tenant-context';
import { useTableManagement } from '../../lib/table-hooks';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { TableWithOrder, MenuItem, CartItemOption } from '../../lib/types';
import { formatCurrency, cn } from '../../lib/utils';
import { ProductDetailModal } from '../../components/restaurant/ProductDetailModal';
import { Button } from '../../components/ui/Button';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
interface WaiterCartItem {
  id: string;
  menuItem: MenuItem;
  quantity: number;
  options: CartItemOption[];
  notes: string;
}
// La Cabaña Parrillera (restaurant_id 24) — Tomar Orden supports
// three modes: Mesa (default, like other restaurants), Para llevar,
// and Express (con datos del cliente, por llamada telefónica).
type WaiterOrderMode = 'mesa' | 'pickup' | 'delivery';
interface WaiterCustomerInfo {
  name: string;
  phone: string;
  address: string;
  maps_url: string;
  notes: string;
  // Cabaña Parrillera — optional express fee (monto del express).
  // Stored as a string while editing so the input stays controllable;
  // converted to a number before submitting / totalizing.
  express_fee: string;
}
const EMPTY_CUSTOMER_INFO: WaiterCustomerInfo = {
  name: '',
  phone: '',
  address: '',
  maps_url: '',
  notes: '',
  express_fee: ''
};
const STORAGE_KEY_PREFIX = 'noiza_waiter_pos_';
function loadPersistedState(restaurantId: string | undefined) {
  if (!restaurantId || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${restaurantId}`);
    if (!raw) return null;
    return JSON.parse(raw) as {
      step: 1 | 2 | 3;
      selectedTable: TableWithOrder | null;
      cart: WaiterCartItem[];
      orderMode?: WaiterOrderMode;
      customerInfo?: WaiterCustomerInfo;
    };
  } catch {
    return null;
  }
}
export function WaiterPOSPage() {
  const navigate = useNavigate();
  const {
    restaurant
  } = useTenant();
  const restaurantId = restaurant?.restaurant_id;
  const {
    tables,
    loading: tablesLoading
  } = useTableManagement(restaurantId);
  const {
    categories
  } = useMenuCategories(restaurantId);
  const {
    items
  } = useMenuItems(restaurantId);
  // Hydrate from localStorage so a refresh doesn't lose the in-progress order
  const persisted = useMemo(() => loadPersistedState(restaurantId), [restaurantId]);
  const [step, setStep] = useState<1 | 2 | 3>(persisted?.step ?? 1);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(persisted?.selectedTable ?? null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<WaiterCartItem[]>(persisted?.cart ?? []);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // ─── Cabaña Parrillera — multi-mode support ──────────────────
  // Only enabled for restaurant_id === '24' / slug 'la-cabana-parrillera'.
  // Every other restaurant keeps the table-only flow it always had.
  const isCabanaParrillera = String(restaurant?.id) === '24' || String(restaurantId) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  const [orderMode, setOrderMode] = useState<WaiterOrderMode>(persisted?.orderMode ?? 'mesa');
  const [customerInfo, setCustomerInfo] = useState<WaiterCustomerInfo>(persisted?.customerInfo ?? EMPTY_CUSTOMER_INFO);
  // Persist whenever step / table / cart / mode / customer info change
  useEffect(() => {
    if (!restaurantId || typeof window === 'undefined') return;
    const key = `${STORAGE_KEY_PREFIX}${restaurantId}`;
    const isEmptyCustomer = !customerInfo.name && !customerInfo.phone && !customerInfo.address && !customerInfo.notes;
    if (cart.length === 0 && !selectedTable && step === 1 && orderMode === 'mesa' && isEmptyCustomer) {
      // Nothing to persist — clear any stale state
      localStorage.removeItem(key);
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        step,
        selectedTable,
        cart,
        orderMode,
        customerInfo
      }));
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }, [restaurantId, step, selectedTable, cart, orderMode, customerInfo]);
  // Filter active categories and items
  const activeCategories = useMemo(() => categories.filter(c => c.is_active).sort((a, b) => a.sort_order - b.sort_order), [categories]);
  const activeItems = useMemo(() => items.filter(i => i.is_active).sort((a, b) => a.sort_order - b.sort_order), [items]);
  // Set initial category
  useEffect(() => {
    if (activeCategories.length > 0 && !activeCategoryId) {
      setActiveCategoryId(activeCategories[0].id);
    }
  }, [activeCategories, activeCategoryId]);
  const cartTotal = cart.reduce((sum, item) => {
    const optionsTotal = item.options.reduce((optSum, opt) => optSum + (Number(opt.price_delta) || 0), 0);
    return sum + (Number(item.menuItem.price) + optionsTotal) * item.quantity;
  }, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  // Cabaña Parrillera — Express fee adds to total only for delivery mode.
  const expressFeeAmount = isCabanaParrillera && orderMode === 'delivery' ? Math.max(0, Number(customerInfo.express_fee) || 0) : 0;
  const orderTotal = cartTotal + expressFeeAmount;
  const handleTableSelect = (table: TableWithOrder) => {
    setSelectedTable(table);
    setStep(2);
  };
  const handleAddToCart = (menuItem: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    setCart(prev => [...prev, {
      id: crypto.randomUUID(),
      menuItem,
      quantity,
      options,
      notes
    }]);
  };
  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? {
          ...item,
          quantity: newQty
        } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  const removeItem = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };
  const handleSubmitOrder = async () => {
    if (!restaurant || cart.length === 0) return;
    // Mesa requires a real table; pickup/delivery don't
    if (orderMode === 'mesa' && !selectedTable) return;
    if (orderMode === 'delivery' && !customerInfo.address.trim()) {
      setSubmitError('La dirección es requerida para pedidos express.');
      return;
    }
    if ((orderMode === 'pickup' || orderMode === 'delivery') && !customerInfo.name.trim()) {
      setSubmitError('El nombre del cliente es requerido.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // ─── La Cabaña Parrillera — merge into existing open table order ───
      // When a table is already occupied with an open order, adding more
      // products from "Tomar Orden" must APPEND to that order, not create
      // a second one. Mirrors CabanaAddItemsModal logic so the KDS sees
      // the new items inside the same comanda.
      const existingOrderId = isCabanaParrillera && orderMode === 'mesa' && selectedTable?.current_order?.id ? selectedTable.current_order.id : null;
      if (existingOrderId) {
        try {
          // 1) Insert each new order_item (same shape as create-order.ts)
          for (const ci of cart) {
            const optTotal = (ci.options || []).reduce((s, o: any) => s + (Number(o.price_delta) || 0), 0);
            const unitPrice = Number(ci.menuItem.price) || 0;
            const lineTotal = (unitPrice + optTotal) * ci.quantity;
            const {
              data: inserted,
              error: itemErr
            } = await supabase.from('order_items').insert({
              order_id: existingOrderId,
              restaurant_id: Number(restaurantId),
              menu_item_id: ci.menuItem.id,
              item_name_snapshot: ci.menuItem.name,
              unit_price_snapshot: unitPrice,
              quantity: ci.quantity,
              notes: ci.notes || null,
              line_total: lineTotal,
              status: 'pending'
            }).select('id').single();
            if (itemErr) throw itemErr;
            if ((ci.options || []).length > 0 && inserted) {
              const optsPayload = ci.options.map((o: any) => ({
                order_item_id: inserted.id,
                restaurant_id: Number(restaurantId),
                option_group: o.option_group || '',
                option_name: o.option_name || '',
                price_delta: Number(o.price_delta) || 0
              }));
              const {
                error: optErr
              } = await supabase.from('order_item_options').insert(optsPayload);
              if (optErr) {
                console.warn('[WAITER POS][cabana-merge] options insert non-blocking:', optErr);
              }
            }
          }
          // 2) Recompute order subtotal/total from surviving items
          const {
            data: allItems,
            error: fetchErr
          } = await supabase.from('order_items').select('line_total, status').eq('order_id', existingOrderId);
          if (fetchErr) throw fetchErr;
          const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
          const {
            data: orderRow
          } = await supabase.from('orders').select('delivery_fee, status').eq('id', existingOrderId).maybeSingle();
          const deliveryFee = Number(orderRow?.delivery_fee) || 0;
          const newTotal = newSubtotal + deliveryFee;
          const updatePayload: Record<string, any> = {
            subtotal: newSubtotal,
            total: newTotal,
            updated_at: new Date().toISOString()
          };
          // If the order was already marked 'ready', bump it back to
          // 'preparing' so KDS shows it again with the new pending items.
          if (orderRow?.status === 'ready') {
            updatePayload.status = 'preparing';
          }
          const {
            error: updErr
          } = await supabase.from('orders').update(updatePayload).eq('id', existingOrderId);
          if (updErr) throw updErr;
        } catch (mergeErr: any) {
          console.error('[WAITER POS][cabana-merge] error:', mergeErr);
          throw new Error(mergeErr?.message || 'No se pudo adicionar a la mesa');
        }
        // Success — same reset flow as the create path below.
        setCart([]);
        setSelectedTable(null);
        setOrderMode('mesa');
        setCustomerInfo(EMPTY_CUSTOMER_INFO);
        setStep(1);
        if (restaurantId && typeof window !== 'undefined') {
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${restaurantId}`);
        }
        return;
      }
      const orderIntentId = crypto.randomUUID();
      let payload: Record<string, any>;
      if (orderMode === 'mesa') {
        payload = {
          restaurant_id: restaurantId,
          order_intent_id: orderIntentId,
          customer_name: `Mesa ${selectedTable!.table_number}`,
          delivery_type: 'dine_in',
          table_id: selectedTable!.id,
          source: 'waiter',
          subtotal: cartTotal,
          total: cartTotal,
          items: cart.map(item => ({
            menu_item_id: item.menuItem.id,
            item_name_snapshot: item.menuItem.name,
            unit_price_snapshot: item.menuItem.price,
            quantity: item.quantity,
            notes: item.notes,
            selected_options: item.options
          }))
        };
      } else {
        const isDelivery = orderMode === 'delivery';
        payload = {
          restaurant_id: restaurantId,
          order_intent_id: orderIntentId,
          customer_name: customerInfo.name.trim(),
          customer_phone: customerInfo.phone.trim() || null,
          delivery_type: isDelivery ? 'delivery' : 'pickup',
          table_id: null,
          delivery_address: isDelivery ? customerInfo.address.trim() || null : null,
          maps_url: isDelivery ? customerInfo.maps_url.trim() || null : null,
          notes: customerInfo.notes.trim() || null,
          payment_method: 'cash',
          source: 'waiter',
          subtotal: cartTotal,
          delivery_fee: isDelivery ? expressFeeAmount : 0,
          total: cartTotal + (isDelivery ? expressFeeAmount : 0),
          items: cart.map(item => ({
            menu_item_id: item.menuItem.id,
            item_name_snapshot: item.menuItem.name,
            unit_price_snapshot: item.menuItem.price,
            quantity: item.quantity,
            notes: item.notes,
            selected_options: item.options
          }))
        };
      }
      console.log('[WAITER POS] orderMode:', orderMode);
      console.log('[WAITER POS] Submitting payload:', payload);
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Error al crear la orden');
      }
      // Success! Reset and go back to step 1
      setCart([]);
      setSelectedTable(null);
      setOrderMode('mesa');
      setCustomerInfo(EMPTY_CUSTOMER_INFO);
      setStep(1);
      if (restaurantId && typeof window !== 'undefined') {
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${restaurantId}`);
      }
    } catch (error: any) {
      console.error('[WAITER POS] Submit error:', error);
      setSubmitError(error.message || 'Error de conexión');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!restaurant) return null;
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-5015">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm" data-id="element-5016">
        <div className="flex items-center gap-3" data-id="element-5017">
          <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center text-white font-bold" data-id="element-5018">
            {restaurant.name.charAt(0)}
          </div>
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-5019">
            {restaurant.name}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-gray-500" data-id="element-5020">
          <span className={cn(step >= 1 ? 'text-brand-charcoal font-bold' : '')} data-id="element-5021">
            1{' '}
            {isCabanaParrillera && orderMode === 'pickup' ? 'Llevar' : isCabanaParrillera && orderMode === 'delivery' ? 'Express' : 'Mesa'}
          </span>
          <span data-id="element-5022">·</span>
          <span className={cn(step >= 2 ? 'text-brand-charcoal font-bold' : '')} data-id="element-5023">
            2 Productos
          </span>
          <span data-id="element-5024">·</span>
          <span className={cn(step >= 3 ? 'text-brand-charcoal font-bold' : '')} data-id="element-5025">
            3 Confirmar
          </span>
        </div>

        <button onClick={() => navigate(`/admin/${restaurant.slug}/operacion`)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" data-id="element-5026">
          <LogOut className="w-4 h-4" data-id="element-5027" />
          <span className="hidden sm:inline" data-id="element-5028">Salir</span>
        </button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-5029">
        {step === 1 && <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-5030">
            <div className="max-w-6xl mx-auto" data-id="element-5031">
              {/* Cabaña Parrillera — Mode selector (Mesa / Para llevar / Express) */}
              {isCabanaParrillera && <div className="mb-8" data-id="element-5032">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-5033">
                    Tipo de orden
                  </h2>
                  <div className="grid grid-cols-3 gap-2 max-w-2xl" data-id="element-5034">
                    {([{
                key: 'mesa',
                label: 'Mesa',
                emoji: '🍽️'
              }, {
                key: 'pickup',
                label: 'Para llevar',
                emoji: '🥡'
              }, {
                key: 'delivery',
                label: 'Express',
                emoji: '🛵'
              }] as {
                key: WaiterOrderMode;
                label: string;
                emoji: string;
              }[]).map(mode => <button key={mode.key} onClick={() => {
                setOrderMode(mode.key);
                if (mode.key !== 'mesa') {
                  setSelectedTable(null);
                }
              }} className={cn('flex flex-col items-center justify-center gap-1 py-4 px-3 rounded-2xl border-2 font-bold transition-all', orderMode === mode.key ? 'bg-brand-charcoal text-white border-brand-charcoal shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400')} data-id="element-5035">
                        <span className="text-2xl leading-none" data-id="element-5036">
                          {mode.emoji}
                        </span>
                        <span className="text-sm" data-id="element-5037">{mode.label}</span>
                      </button>)}
                  </div>
                </div>}

              {/* Cabaña Parrillera — Customer info form (Para llevar / Express) */}
              {isCabanaParrillera && orderMode !== 'mesa' && <div className="mb-8 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 max-w-2xl" data-id="element-5038">
                  <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-5039">
                    {orderMode === 'delivery' ? 'Datos del cliente (Express)' : 'Datos del cliente (Para llevar)'}
                  </h2>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-5040">
                    {orderMode === 'delivery' ? 'Información que vas a anotar de la llamada para el repartidor.' : 'Información para identificar la orden cuando el cliente venga a retirarla.'}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5041">
                    <div className="sm:col-span-1" data-id="element-5042">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-5043">
                        Nombre del cliente *
                      </label>
                      <input type="text" value={customerInfo.name} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  name: e.target.value
                }))} placeholder="Ej: María Pérez" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-5044" />
                    </div>

                    <div className="sm:col-span-1" data-id="element-5045">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-5046">
                        Teléfono {orderMode === 'delivery' && '*'}
                      </label>
                      <input type="tel" inputMode="tel" value={customerInfo.phone} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} placeholder="8888-8888" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-5047" />
                    </div>

                    {orderMode === 'delivery' && <>
                        <div className="sm:col-span-2" data-id="element-5048">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-5049">
                            Dirección de entrega *
                          </label>
                          <textarea value={customerInfo.address} onChange={e => setCustomerInfo(prev => ({
                    ...prev,
                    address: e.target.value
                  }))} placeholder="Ej: 200m sur de la iglesia, casa portón verde…" rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent resize-none" data-id="element-5050" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-5051">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-5052">
                            Link de Google Maps (opcional)
                          </label>
                          <input type="url" inputMode="url" value={customerInfo.maps_url} onChange={e => setCustomerInfo(prev => ({
                    ...prev,
                    maps_url: e.target.value
                  }))} placeholder="https://maps.google.com/…" className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-5053" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-5054">
                          <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-5055">
                            Monto de express (opcional)
                          </label>
                          <div className="relative" data-id="element-5056">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold pointer-events-none" data-id="element-5057">
                              ₡
                            </span>
                            <input type="number" inputMode="decimal" min="0" step="100" value={customerInfo.express_fee} onChange={e => setCustomerInfo(prev => ({
                      ...prev,
                      express_fee: e.target.value
                    }))} placeholder="Ej: 1500" className="w-full h-11 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-5058" />
                          </div>
                          <p className="mt-1.5 text-xs text-gray-500" data-id="element-5059">
                            Se sumará al total de la orden si se ingresa.
                          </p>
                        </div>
                      </>}

                    <div className="sm:col-span-2" data-id="element-5060">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-5061">
                        Notas (opcional)
                      </label>
                      <textarea value={customerInfo.notes} onChange={e => setCustomerInfo(prev => ({
                  ...prev,
                  notes: e.target.value
                }))} placeholder="Ej: tocar el timbre, sin cebolla, etc." rows={2} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent resize-none" data-id="element-5062" />
                    </div>
                  </div>

                  <button onClick={() => {
              if (!customerInfo.name.trim()) return;
              if (orderMode === 'delivery' && (!customerInfo.address.trim() || !customerInfo.phone.trim())) return;
              setStep(2);
            }} disabled={!customerInfo.name.trim() || orderMode === 'delivery' && (!customerInfo.address.trim() || !customerInfo.phone.trim())} className={cn('mt-5 w-full h-12 rounded-xl font-bold text-base transition-all', customerInfo.name.trim() && (orderMode !== 'delivery' || customerInfo.address.trim() && customerInfo.phone.trim()) ? 'bg-green-500 hover:bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-5063">
                    Continuar a productos →
                  </button>
                </div>}

              {/* Tables grid — only when in 'mesa' mode (or any non-Cabaña restaurant) */}
              {(!isCabanaParrillera || orderMode === 'mesa') && <>
                  <h1 className="text-3xl font-black text-gray-900 mb-2" data-id="element-5064">
                    Selecciona una mesa
                  </h1>
                  <p className="text-gray-500 mb-8" data-id="element-5065">
                    Toca una mesa para tomar la orden
                  </p>

                  {tablesLoading ? <div className="flex items-center justify-center py-20" data-id="element-5066">
                      <div className="w-8 h-8 border-4 border-brand-charcoal border-t-transparent rounded-full animate-spin" data-id="element-5067"></div>
                    </div> : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-id="element-5068">
                      {tables.map(table => {
                const isOccupied = table.status !== 'free';
                return <button key={table.id} onClick={() => handleTableSelect(table)} className={cn('flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all min-h-[140px]', isOccupied ? 'bg-amber-50 border-amber-200 hover:border-amber-400' : 'bg-white border-gray-100 hover:border-green-500 hover:shadow-md')} data-id="element-5069">
                            <span className="text-5xl font-black text-gray-900 mb-2" data-id="element-5070">
                              {table.table_number}
                            </span>
                            {table.table_name && <span className="text-xs font-medium text-gray-500 mb-3 text-center line-clamp-1" data-id="element-5071">
                                {table.table_name}
                              </span>}
                            <div className={cn('px-3 py-1 rounded-full text-xs font-bold', isOccupied ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')} data-id="element-5072">
                              {isOccupied ? 'Ocupada' : 'Libre'}
                            </div>
                          </button>;
              })}
                    </div>}
                </>}
            </div>
          </div>}

        {step === 2 && (selectedTable || isCabanaParrillera && orderMode !== 'mesa') && <div className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-5073">
              {/* Sub-header */}
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0" data-id="element-5074">
                <div className="flex items-center gap-3" data-id="element-5075">
                  <button onClick={() => setStep(1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-5076">
                    <ArrowLeft className="w-6 h-6" data-id="element-5077" />
                  </button>
                  <div data-id="element-5078">
                    <h2 className="text-xl font-bold text-gray-900 leading-none" data-id="element-5079">
                      {selectedTable ? `Mesa ${selectedTable.table_number}` : orderMode === 'delivery' ? `Express — ${customerInfo.name || 'Cliente'}` : `Para llevar — ${customerInfo.name || 'Cliente'}`}
                    </h2>
                    {selectedTable && selectedTable.table_name && <span className="text-sm text-gray-500" data-id="element-5080">
                        {selectedTable.table_name}
                      </span>}
                    {!selectedTable && customerInfo.phone && <span className="text-sm text-gray-500" data-id="element-5081">
                        📞 {customerInfo.phone}
                      </span>}
                  </div>
                </div>

                <button onClick={() => setStep(3)} disabled={cart.length === 0} className={cn('flex items-center gap-3 px-4 py-2 rounded-xl font-bold transition-all', cart.length > 0 ? 'bg-brand-charcoal text-white hover:bg-gray-800 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-5082">
                  <div className="flex items-center gap-2" data-id="element-5083">
                    <ShoppingBag className="w-5 h-5" data-id="element-5084" />
                    <span className="bg-white/20 px-2 py-0.5 rounded-md text-sm" data-id="element-5085">
                      {cartItemCount}
                    </span>
                  </div>
                  <span data-id="element-5086">{formatCurrency(orderTotal)}</span>
                </button>
              </div>

              {/* Search */}
              <div className="bg-white border-b border-gray-100 px-4 py-3 shrink-0" data-id="element-5087">
                <div className="relative max-w-2xl mx-auto" data-id="element-5088">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-5089" />
                  <input type="text" inputMode="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar producto…" className="w-full h-12 pl-12 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-charcoal focus:border-transparent" data-id="element-5090" />
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full" aria-label="Limpiar búsqueda" data-id="element-5091">
                      <X className="w-4 h-4" data-id="element-5092" />
                    </button>}
                </div>
              </div>

              {/* Categories — hide while searching */}
              {!searchQuery.trim() && <div className="bg-white border-b border-gray-100 overflow-x-auto shrink-0 hide-scrollbar" data-id="element-5093">
                  <div className="flex p-2 gap-2 min-w-max" data-id="element-5094">
                    {activeCategories.map(cat => <button key={cat.id} onClick={() => setActiveCategoryId(cat.id)} className={cn('px-6 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap', activeCategoryId === cat.id ? 'bg-brand-charcoal text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100')} data-id="element-5095">
                        {cat.name}
                      </button>)}
                  </div>
                </div>}

              {/* Items Grid */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-5096">
                {(() => {
            const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const q = normalize(searchQuery.trim());
            const filteredItems = q ? activeItems.filter(item => {
              const haystack = normalize(`${item.name} ${item.description || ''}`);
              return haystack.includes(q);
            }) : activeItems.filter(item => item.category_id === activeCategoryId);
            if (filteredItems.length === 0) {
              return <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-5097">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-5098">
                          <Search className="w-7 h-7 text-gray-400" data-id="element-5099" />
                        </div>
                        <p className="text-lg font-bold text-gray-900 mb-1" data-id="element-5100">
                          {q ? 'Sin resultados' : 'No hay productos'}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-5101">
                          {q ? `No encontramos productos para "${searchQuery}"` : 'No hay productos en esta categoría'}
                        </p>
                      </div>;
            }
            return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-5102">
                      {filteredItems.map(item => {
                const cartItem = cart.find(c => c.menuItem.id === item.id);
                return <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col h-full" data-id="element-5103">
                            <div className="flex-1" data-id="element-5104">
                              <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 line-clamp-2" data-id="element-5105">
                                {item.name}
                              </h3>
                              {item.description && <p className="text-sm text-gray-500 line-clamp-2 mb-3" data-id="element-5106">
                                  {item.description}
                                </p>}
                            </div>
                            <div className="flex items-end justify-between mt-4" data-id="element-5107">
                              <span className="font-bold text-xl text-green-600" data-id="element-5108">
                                {formatCurrency(Number(item.price))}
                              </span>

                              {cartItem && !item.is_customizable ? <div className="flex items-center bg-gray-100 rounded-full p-1" data-id="element-5109">
                                  <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, -1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-5110">
                                    <Minus className="w-4 h-4" data-id="element-5111" />
                                  </button>
                                  <span className="w-8 text-center font-bold text-gray-900" data-id="element-5112">
                                    {cartItem.quantity}
                                  </span>
                                  <button onClick={e => {
                        e.stopPropagation();
                        updateQuantity(cartItem.id, 1);
                      }} className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-gray-600 shadow-sm" data-id="element-5113">
                                    <Plus className="w-4 h-4" data-id="element-5114" />
                                  </button>
                                </div> : <button onClick={() => setSelectedProduct(item)} className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors" data-id="element-5115">
                                  <Plus className="w-6 h-6" data-id="element-5116" />
                                </button>}
                            </div>
                          </div>;
              })}
                    </div>;
          })()}
              </div>
            </div>}

        {step === 3 && (selectedTable || isCabanaParrillera && orderMode !== 'mesa') && <div className="flex-1 flex flex-col h-full overflow-hidden bg-white" data-id="element-5117">
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shrink-0" data-id="element-5118">
                <button onClick={() => setStep(2)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-5119">
                  <ArrowLeft className="w-6 h-6" data-id="element-5120" />
                </button>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-5121">
                  {selectedTable ? `Confirmar Orden - Mesa ${selectedTable.table_number}` : orderMode === 'delivery' ? `Confirmar Express — ${customerInfo.name || 'Cliente'}` : `Confirmar Para Llevar — ${customerInfo.name || 'Cliente'}`}
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-id="element-5122">
                <div className="max-w-3xl mx-auto" data-id="element-5123">
                  {submitError && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-800" data-id="element-5124">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-5125" />
                      <p className="text-sm font-medium" data-id="element-5126">{submitError}</p>
                    </div>}

                  <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden mb-6" data-id="element-5127">
                    <div className="divide-y divide-gray-100" data-id="element-5128">
                      {cart.map(item => {
                  const optionsTotal = item.options.reduce((sum, opt) => sum + (Number(opt.price_delta) || 0), 0);
                  const itemTotal = (Number(item.menuItem.price) + optionsTotal) * item.quantity;
                  return <div key={item.id} className="p-4 flex gap-4" data-id="element-5129">
                            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 h-fit shrink-0" data-id="element-5130">
                              <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-5131">
                                <Minus className="w-4 h-4" data-id="element-5132" />
                              </button>
                              <span className="w-8 text-center font-bold text-gray-900" data-id="element-5133">
                                {item.quantity}
                              </span>
                              <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-md" data-id="element-5134">
                                <Plus className="w-4 h-4" data-id="element-5135" />
                              </button>
                            </div>

                            <div className="flex-1 min-w-0" data-id="element-5136">
                              <div className="flex justify-between items-start gap-4" data-id="element-5137">
                                <h4 className="font-bold text-gray-900 text-lg" data-id="element-5138">
                                  {item.menuItem.name}
                                </h4>
                                <span className="font-bold text-gray-900 whitespace-nowrap" data-id="element-5139">
                                  {formatCurrency(itemTotal)}
                                </span>
                              </div>

                              {item.options.length > 0 && <ul className="mt-1 space-y-0.5" data-id="element-5140">
                                  {item.options.map((opt, idx) => <li key={idx} className="text-sm text-gray-500 flex justify-between" data-id="element-5141">
                                      <span data-id="element-5142">• {opt.option_name}</span>
                                      {Number(opt.price_delta) > 0 && <span data-id="element-5143">
                                          +
                                          {formatCurrency(Number(opt.price_delta))}
                                        </span>}
                                    </li>)}
                                </ul>}

                              {item.notes && <p className="mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded-lg" data-id="element-5144">
                                  Nota: {item.notes}
                                </p>}
                            </div>

                            <button onClick={() => removeItem(item.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg h-fit transition-colors" data-id="element-5145">
                              <X className="w-5 h-5" data-id="element-5146" />
                            </button>
                          </div>;
                })}
                    </div>

                    <div className="p-4 bg-white border-t border-gray-100 space-y-2" data-id="element-5147">
                      {expressFeeAmount > 0 && <>
                          <div className="flex justify-between items-center text-base" data-id="element-5148">
                            <span className="text-gray-500" data-id="element-5149">Subtotal</span>
                            <span className="font-medium text-gray-700" data-id="element-5150">
                              {formatCurrency(cartTotal)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-base" data-id="element-5151">
                            <span className="text-gray-500" data-id="element-5152">
                              Monto de express
                            </span>
                            <span className="font-medium text-gray-700" data-id="element-5153">
                              {formatCurrency(expressFeeAmount)}
                            </span>
                          </div>
                          <div className="border-t border-gray-100 pt-2" data-id="element-5154" />
                        </>}
                      <div className="flex justify-between items-center" data-id="element-5155">
                        <span className="text-lg font-medium text-gray-500" data-id="element-5156">
                          Total a cobrar
                        </span>
                        <span className="text-3xl font-black text-gray-900" data-id="element-5157">
                          {formatCurrency(orderTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSubmitOrder} disabled={isSubmitting || cart.length === 0} className="w-full h-16 text-xl font-bold bg-green-500 hover:bg-green-600 text-white rounded-2xl" data-id="element-5158">
                    {isSubmitting ? <div className="flex items-center gap-2" data-id="element-5159">
                        <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" data-id="element-5160" />
                        <span data-id="element-5161">Enviando a cocina...</span>
                      </div> : <div className="flex items-center gap-2" data-id="element-5162">
                        <Check className="w-6 h-6" data-id="element-5163" />
                        <span data-id="element-5164">Confirmar y Enviar Orden</span>
                      </div>}
                  </Button>
                </div>
              </div>
            </div>}
      </main>

      {/* Product Detail Modal */}
      {selectedProduct && <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} item={selectedProduct} onAdd={handleAddToCart} data-id="element-5165" />}
    </div>;
}